import { useEffect, useRef } from "react";
import { Check, X } from "lucide-react";

import { Slider } from "@/design-system/primitives/slider";
import { distanceLabel } from "@/listings/listing.format.ts";
import type { AreaTool } from "@/search/ConsolePanel.tsx";
import type { DraftCircle } from "@/map/MapCanvas.tsx";

/**
 * Mobile-only area editor. On a phone the drawer covers the map, so opening a
 * tool closes the drawer and hands control to this dock — the map stays fully
 * visible while you place a circle or tap districts, which is the whole point of
 * those tools.
 */
export function ToolDock({
  tool,
  draftCircle,
  onDraftCircleChange,
  draftCount,
  onApply,
  onCancel,
  onHeightChange,
}: {
  tool: Exclude<AreaTool, "none">;
  draftCircle: DraftCircle | null;
  onDraftCircleChange: (c: DraftCircle) => void;
  draftCount: number;
  onApply: () => void;
  onCancel: () => void;
  /** Reports the dock's live height so the map controls can clear it. */
  onHeightChange?: (px: number) => void;
}) {
  const canApply = tool === "radius" ? draftCircle !== null : draftCount > 0;

  // The dock grows and shrinks with its contents (the radius slider appears only
  // once a centre exists), so its height is measured rather than assumed.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !onHeightChange) return;
    const ro = new ResizeObserver(() => onHeightChange(el.offsetHeight));
    ro.observe(el);
    onHeightChange(el.offsetHeight);
    return () => {
      ro.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange]);

  return (
    <div
      ref={ref}
      className="console console-grid pointer-events-auto fixed inset-x-2 bottom-2 z-30 rounded-console p-3 md:hidden"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-marine-ink">
          {tool === "radius" ? "Radius tool" : "District tool"}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-chalk-mute">
          {tool === "radius"
            ? draftCircle
              ? distanceLabel(draftCircle.radiusM)
              : "tap the map"
            : `${draftCount} selected`}
        </span>
      </div>

      {tool === "radius" && draftCircle && (
        <Slider
          value={[Math.round(draftCircle.radiusM)]}
          min={150}
          max={12000}
          step={50}
          onValueChange={([v]) => onDraftCircleChange({ ...draftCircle, radiusM: v })}
          aria-label="Radius in metres"
          className="mb-3 [&_[data-slot=slider-range]]:bg-marine-ink [&_[data-slot=slider-thumb]]:size-5 [&_[data-slot=slider-thumb]]:border-marine-ink [&_[data-slot=slider-thumb]]:bg-ink-900 [&_[data-slot=slider-track]]:h-1.5 [&_[data-slot=slider-track]]:bg-ink-600"
        />
      )}

      {tool === "districts" && (
        <p className="mb-3 text-[11.5px] leading-snug text-chalk-dim">
          Tap districts on the map to select them.
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 rounded-lg border border-rule px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-chalk-dim"
        >
          <X className="size-3.5" />
          Cancel
        </button>
        <button
          type="button"
          disabled={!canApply}
          onClick={onApply}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-marine px-3 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-on-accent transition disabled:bg-ink-600 disabled:text-chalk-mute"
        >
          <Check className="size-3.5" />
          Apply
        </button>
      </div>
    </div>
  );
}
